<template>
    <div class="ld-container">
        <div class="hi-header">
            <HeaderBox :title="$t('휴일 관리')" />
        </div>
        <div class="hi-body">
            <HolidayCalendar
                :year="year"
                :month="month"
                :holidays="holidayList"
                :holidaysByYear="holidayByYear"
                :publicCode="publicCode"
                :loading="loading"
                @change="handleMonthChange"
                @year-preview="loadYearOnce"
                @date-click="handleDateClick"
                @event-click="handleEventClick"
            />
        </div>
        <HolidayDetail
            ref="HolidayDetail"
            :title="'휴일'"
            :holiTypeCodeList="holiTypeCodeList"
            @data-updated="handleDetailUpdated"
        />
    </div>
</template>

<script>
import dayjs from "dayjs";
import HeaderBox from "../../component/headerBox/management/Header.vue";
import HolidayCalendar from "./HolidayCalendar.vue";
import HolidayDetail from "./HolidayDetail.vue";
import backEndApi from "../../../api/backEndApi";
import { StatusCodes } from "http-status-codes";
import * as popupMessages from "@src/consts/popupMessageConsts";
import AppErrorHandler from "@src/appErrorHandler";

export default {
    props: ["type"],
    components: { HeaderBox, HolidayCalendar, HolidayDetail },
    data() {
        const now = dayjs();
        return {
            year: now.year(),
            month: now.month() + 1,
            holidayByYear: {}, // { [year]: holiday[] }
            holiTypeCodeList: [],
            publicCode: "A001", // 법정 공휴일 (수정 불가)
            etcCode: "A002", // 기타 휴일 (등록 시 고정)
            loading: false,
            popupMessages,
            syncDate: "",
        };
    },
    computed: {
        // 달력에 표시되는 이전/다음 달 날짜까지 포함한 연도 목록
        visibleYears() {
            const first = dayjs(new Date(this.year, this.month - 1, 1));
            const last = first.endOf("month");
            return [
                ...new Set([
                    first.subtract(first.day(), "day").year(),
                    this.year,
                    last.add(6 - last.day(), "day").year(),
                ]),
            ];
        },
        holidayList() {
            return this.visibleYears.flatMap((y) => this.holidayByYear[y] || []);
        },
    },
    created() {
        this.getHoliTypeCodeList();
    },
    async mounted() {
        await this.loadVisibleYears();
    },
    methods: {
        async getHoliTypeCodeList() {
            try {
                const result = await backEndApi.publicCode.getSmallCodeList("holiTypeCode");
                if (result.data && result.data.length > 0) {
                    this.holiTypeCodeList = result.data;
                }
            } catch (e) {
                console.error(e);
            }
        },
        async loadVisibleYears(force = false) {
            const years = this.visibleYears.filter((y) => force || !this.holidayByYear[y]);
            if (years.length === 0) return;
            this.loading = true;
            try {
                await Promise.all(years.map((y) => this.fetchYear(y)));
            } finally {
                this.loading = false;
            }
        },
        async fetchYear(year) {
            try {
                const result = await backEndApi.calendar.getHolidayList({ baseDate: year, holiTypeCode: null });
                if (result.status == 200) {
                    this.$set(this.holidayByYear, year, result.data || []);
                } else {
                    this.alertWarning(
                        `${result.data?.message || "조회에 실패했습니다."}`,
                        "",
                        `${typeof result.data?.detail == "string" ? result.data.detail : ""}`,
                    );
                }
            } catch (err) {
                new AppErrorHandler(err)
                    .setOtherMessage(popupMessages.COMMON_API_CALL_FAIL_MESSAGE, this.alertDanger)
                    .createApiHandle(StatusCodes.INTERNAL_SERVER_ERROR)
                    .createBizHandle(
                        this.ERR_KIND.MISSING_DATA,
                        popupMessages.COMMON_SEARCH_NO_DATA_POPUP_MESSAGE,
                        this.alertNoti,
                    )
                    .errHandling(true);
            }
        },
        // 연/월 선택 패널에서 다른 연도를 볼 때 해당 연도의 휴일 조회
        async loadYearOnce(year) {
            if (!this.holidayByYear[year]) await this.fetchYear(year);
        },
        async handleMonthChange({ year, month }) {
            this.year = year;
            this.month = month;
            await this.loadVisibleYears();
        },
        handleDateClick(date) {
            this.$refs["HolidayDetail"].newData(date, this.etcCode);
        },
        handleEventClick(row) {
            if (row.holiTypeCode === this.publicCode) return;
            this.$refs["HolidayDetail"].loadDataFromRow(row);
        },
        async handleDetailUpdated() {
            await this.loadVisibleYears(true);
        },
    },
};
</script>

<style scoped>
.ld-container {
    display: flex;
    flex-direction: column;
    height: 90vh;
    width: 100%;
    background-color: white;
    border-radius: 10px;
    overflow: hidden;
    padding: 10px 30px 10px 20px;
}
.hi-header {
    flex: 0 0 auto;
    margin-bottom: 10px;
}
.hi-notice {
    margin-top: 6px;
}
.hi-body {
    flex: 1;
    min-height: 0;
}
</style>
